/**
 * Service Status catalog maintenance (Phase 1).
 * Source of truth for status labels, ordering, and selectability. System rows
 * (enum-backed) cannot be deleted and their code is read-only.
 */
import React, { useState, useEffect } from 'react';
import { ListChecks, Plus, RefreshCw, Edit2, Trash2 } from 'lucide-react';
import { dealerService } from '../services/dealer.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';

const emptyForm = {
  code: '',
  label: '',
  description: '',
  display_order: 0,
  is_terminal: false,
  is_active: true,
  color: '',
};

function StatusModal({ isOpen, onClose, onSave, status }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isSystem = !!status?.is_system;

  useEffect(() => {
    setForm(status
      ? {
          code: status.code || '',
          label: status.label || '',
          description: status.description || '',
          display_order: status.display_order ?? 0,
          is_terminal: !!status.is_terminal,
          is_active: status.is_active !== false,
          color: status.color || '',
        }
      : emptyForm);
    setError('');
  }, [status, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        display_order: parseInt(form.display_order) || 0,
        color: form.color || null,
        description: form.description || null,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save status.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">{status ? 'Edit Status' : 'New Status'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <input name="code" value={form.code} onChange={handleChange} required
                disabled={isSystem}
                placeholder="e.g. assigned"
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
              {isSystem && <p className="text-xs text-gray-400 mt-1">System status — code locked.</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" name="display_order" value={form.display_order} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
            <input name="label" value={form.label} onChange={handleChange} required
              placeholder="e.g. Assigned"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Color (optional)</label>
            <input name="color" value={form.color} onChange={handleChange}
              placeholder="#16a34a or tailwind class"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" name="is_terminal" checked={form.is_terminal} onChange={handleChange} /> Terminal
            </label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ServiceStatuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const data = await dealerService.getServiceStatuses();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching service statuses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (payload) => {
    if (editing) {
      await dealerService.updateServiceStatus(editing.id, payload);
    } else {
      await dealerService.createServiceStatus(payload);
    }
    fetchStatuses();
  };

  const confirmDelete = async () => {
    try {
      await dealerService.deleteServiceStatus(deleteId);
      setDeleteId(null);
      fetchStatuses();
    } catch (err) {
      console.error('Error deleting status:', err);
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ListChecks className="w-6 h-6" /> Service Statuses
        </h1>
        <div className="flex gap-2">
          <button onClick={fetchStatuses}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setEditing(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
            <Plus className="w-4 h-4" /> Add Status
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg bxshadowhd p-2 mrgall border">
        {loading ? <LoadingSpinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Order', 'Code', 'Label', 'Terminal', 'Active', 'System', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left font-medium text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {statuses.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-6 text-center text-gray-500">No statuses found</td></tr>
                ) : statuses.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-gray-500">{s.display_order}</td>
                    <td className="px-3 py-3 font-mono text-gray-800">{s.code}</td>
                    <td className="px-3 py-3 text-gray-900">{s.label}</td>
                    <td className="px-3 py-3 text-gray-600">{s.is_terminal ? 'Yes' : '—'}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-1 text-xs rounded-md ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-500">{s.is_system ? 'System' : 'Custom'}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => { setEditing(s); setShowModal(true); }} className="text-blue-600 hover:text-blue-800">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(s.id)}
                          disabled={s.is_system}
                          title={s.is_system ? 'System status — deactivate instead' : 'Delete'}
                          className="text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <StatusModal isOpen={showModal} onClose={() => setShowModal(false)} onSave={handleSave} status={editing} />
      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={confirmDelete}
        title="Delete Status" message="Delete this service status?" />
    </div>
  );
};

export default ServiceStatuses;
